/**
 * iter212i.c — Preview Fault-Lab Console
 *
 * Admin-only page that surfaces every /preview/* control the fault-lab
 * exposes so Codex can operate the acceptance suite without extracting
 * a browser JWT. Every request rides the signed-in axios session; no
 * tokens leave the tab, no public URLs, no clipboard fumbling.
 *
 * Sections (top → bottom):
 *   1. Live state — preview/state + isolation block, 15 s auto refresh
 *   2. Armed faults table — per-row disarm, plus "disarm all"
 *   3. Arm form — synthetic intake + mode + ttl + note
 *   4. Fixture prep — seed projection + emit event
 *   5. Cleanup — remove synthetic rows, per-intake or all
 *
 * SAFETY:
 *   * page lives at /app/admin/gh-preview-fault-lab and is
 *     protected by the existing admin route guard in App.js
 *   * every write endpoint enforces the synthetic_prefix + env flag
 *     on the backend, so a stray click cannot touch real intakes
 *   * "arm" and "emit event" both have a confirm-before-fire
 *   * disarm-all requires the operator to type CONFIRM
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  RefreshCw, Zap, ZapOff, ShieldCheck, ShieldAlert, Trash2, Home,
  Loader2, Plus, Rocket, Database, AlertTriangle,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PREVIEW = `${API}/integrations/garage-hive/preview`;

const MODE_OPTIONS = [
  { value: "http_500",        label: "HTTP 500 (server rejection)" },
  { value: "slow_timeout",    label: "slow_timeout (real transport failure — recommended)" },
  { value: "malformed_json",  label: "Malformed JSON (truncated body)" },
  { value: "transport_close", label: "transport_close (CDN may normalise — see playbook)" },
];

const SYNTH_HINT = "^preview_synth_[a-z0-9][a-z0-9_-]{2,63}$";

export default function GHPreviewFaultLabPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState(null);
  const [armed, setArmed] = useState([]);
  const [error, setError] = useState(null);

  // ---- forms ---------------------------------------------------------------
  const [armForm, setArmForm] = useState({
    intake_id: "preview_synth_abc_beta",
    mode: "slow_timeout",
    ttl_minutes: 30,
    note: "",
  });
  const [seedForm, setSeedForm] = useState({
    intake_id: "preview_synth_abc_alpha",
    state_code: "portal_sent",
    gh_jobsheet_id: "BC-A-9001",
    gh_jobsheet_number: "",
    registration: "PRV01A",
  });
  const [eventForm, setEventForm] = useState({
    intake_id: "preview_synth_abc_alpha",
    event_type: "state_transition",
    hint_state: "portal_sent",
    reason: "acceptance-projection-retry",
  });
  const [cleanupIds, setCleanupIds] = useState("");
  const [disarmAllConfirm, setDisarmAllConfirm] = useState("");

  // ---- loaders -------------------------------------------------------------
  const loadState = useCallback(async () => {
    try {
      const [s, a] = await Promise.all([
        axios.get(`${PREVIEW}/state`),
        axios.get(`${PREVIEW}/fault-lab`),
      ]);
      setState(s.data);
      setArmed(a.data?.armed || []);
      setError(null);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Failed to load fault-lab state.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadState(); }, [loadState]);
  useEffect(() => {
    const t = setInterval(loadState, 15000);
    return () => clearInterval(t);
  }, [loadState]);

  // ---- actions -------------------------------------------------------------
  const doArm = async () => {
    if (!/^preview_synth_[a-z0-9][a-z0-9_-]{2,63}$/.test(armForm.intake_id)) {
      toast.error(`intake_id must match ${SYNTH_HINT}`);
      return;
    }
    if (!window.confirm(
      `Arm ${armForm.mode} on ${armForm.intake_id} for ${armForm.ttl_minutes} min?`
    )) return;
    try {
      const r = await axios.post(`${PREVIEW}/fault-lab/arm`, armForm);
      toast.success(`Armed ${r.data.mode} on ${r.data.intake_id} (expires ${r.data.expires_at})`);
      loadState();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "arm failed");
    }
  };

  const doDisarm = async (intake_id) => {
    try {
      const r = await axios.post(`${PREVIEW}/fault-lab/disarm`, { intake_id });
      toast.success(`Disarmed ${intake_id} (rows ${r.data.disarmed})`);
      loadState();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "disarm failed");
    }
  };

  const doDisarmAll = async () => {
    if (disarmAllConfirm !== "CONFIRM") {
      toast.error("Type CONFIRM to disarm every intake");
      return;
    }
    try {
      const r = await axios.delete(`${PREVIEW}/fault-lab/all`);
      toast.success(`Disarmed ${r.data.disarmed} arm(s)`);
      setDisarmAllConfirm("");
      loadState();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "disarm-all failed");
    }
  };

  const doSeed = async () => {
    if (!/^preview_synth_[a-z0-9][a-z0-9_-]{2,63}$/.test(seedForm.intake_id)) {
      toast.error(`intake_id must match ${SYNTH_HINT}`);
      return;
    }
    try {
      await axios.post(`${PREVIEW}/fixtures/seed-projection`, {
        ...seedForm,
        gh_jobsheet_number: seedForm.gh_jobsheet_number || null,
      });
      toast.success(`Projection seeded for ${seedForm.intake_id}`);
      loadState();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "seed failed");
    }
  };

  const doEmit = async () => {
    if (!/^preview_synth_[a-z0-9][a-z0-9_-]{2,63}$/.test(eventForm.intake_id)) {
      toast.error(`intake_id must match ${SYNTH_HINT}`);
      return;
    }
    if (!window.confirm(
      `Emit ${eventForm.event_type} event on ${eventForm.intake_id}? A fresh sequence will be allocated.`
    )) return;
    try {
      const r = await axios.post(`${PREVIEW}/fixtures/emit-event`, eventForm);
      toast.success(`Event emitted at sequence ${r.data.sequence}`);
      loadState();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "emit failed");
    }
  };

  const doCleanup = async ({ all }) => {
    const body = all
      ? { all: true }
      : { intake_ids: cleanupIds.split(/\s+|,/).map(s => s.trim()).filter(Boolean) };
    if (!all && !body.intake_ids.length) {
      toast.error("Provide at least one intake_id (space or comma-separated)");
      return;
    }
    if (!window.confirm(
      all
        ? "Remove EVERY synthetic event, projection and fixture row?"
        : `Remove synthetic rows for: ${body.intake_ids.join(", ")}?`
    )) return;
    try {
      const r = await axios.post(`${PREVIEW}/fixtures/cleanup`, body);
      toast.success(
        `Cleanup: ${r.data.removed_events} events, ` +
        `${r.data.removed_projections} projections, ` +
        `${r.data.removed_fixtures} fixtures, ${r.data.disarmed} arms.`
      );
      setCleanupIds("");
      loadState();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "cleanup failed");
    }
  };

  // ---- render --------------------------------------------------------------
  const isolation = state?.isolation || null;
  const armedCount = state?.armed_count ?? 0;
  const synth = state ? (state.synthetic_events + state.synthetic_projections) : 0;
  const disarmedOK = armedCount === 0;
  const cleanOK = synth === 0;
  const enabled = state?.enabled;

  const badgeTone = useMemo(() => {
    if (!enabled) return "bg-slate-100 border-slate-300 text-slate-700";
    if (armedCount > 0) return "bg-amber-50 border-amber-300 text-amber-800";
    return "bg-emerald-50 border-emerald-300 text-emerald-800";
  }, [enabled, armedCount]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-teal-700" />
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 pb-16" data-testid="gh-preview-fault-lab-page">
      <div className="max-w-5xl mx-auto px-5 py-6">
        <div className="flex justify-between items-start mb-5">
          <div>
            <div className="inline-flex items-center gap-1.5 mb-1.5 px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200">
              <Zap className="w-3 h-3 text-slate-600" />
              <p className="text-[10px] uppercase tracking-[0.22em] text-slate-700 font-semibold">
                Admin · GH Preview Fault-Lab
              </p>
            </div>
            <h1 className="text-[26px] sm:text-[30px] font-bold tracking-tight text-slate-900">
              Fault-Lab Console
            </h1>
            <p className="text-slate-600 mt-1 text-sm">
              Arm / disarm / seed / emit / cleanup — all through this admin session.
              No tokens leave your browser.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={loadState} data-testid="fl-refresh">
              <RefreshCw className="w-4 h-4 mr-1.5" />Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/app/admin/integration-health")}
                    data-testid="fl-back">
              <Home className="w-4 h-4 mr-1.5" />Health
            </Button>
          </div>
        </div>

        {error && (
          <div className="rounded-lg border-2 border-red-300 bg-red-50 p-3 mb-4 text-sm text-red-800">
            {error}
          </div>
        )}

        {/* Live state */}
        <section className={`rounded-xl border-2 px-5 py-4 mb-5 ${badgeTone}`}
                 data-testid="fl-state-banner">
          <div className="flex items-center gap-3 mb-2">
            {disarmedOK
              ? <ShieldCheck className="w-6 h-6 shrink-0" />
              : <ShieldAlert className="w-6 h-6 shrink-0" />}
            <div>
              <p className="text-[10px] uppercase font-bold tracking-widest">Live state</p>
              <p className="text-lg font-semibold">
                {enabled ? "Fault-lab ENABLED" : "Fault-lab DISABLED (env flag unset)"} · {armedCount} arm(s) · {synth} synthetic row(s)
              </p>
            </div>
          </div>
          {state && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] mt-3">
              <KV label="Max event seq" value={state.current_max_event_sequence} mono />
              <KV label="Total events"  value={state.total_events} mono />
              <KV label="Synthetic events" value={state.synthetic_events} mono />
              <KV label="Synthetic projections" value={state.synthetic_projections} mono />
              {isolation && (<>
                <KV label="GH BC env" value={isolation.gh_environment_name || "—"} />
                <KV label="Yestimate deployment" value={isolation.yestimate_deployment} />
                <KV label="DB name" value={isolation.db_name} mono />
                <KV label="Rows outside allowlist"
                    value={isolation.synthetic_events_out_of_allowlist}
                    tone={isolation.synthetic_events_out_of_allowlist === 0 ? "good" : "bad"} />
              </>)}
            </div>
          )}
          {isolation?.gh_environment_note && (
            <p className="mt-3 text-[11px] text-slate-600 leading-snug">
              <AlertTriangle className="inline w-3 h-3 mr-1 text-amber-600" />
              {isolation.gh_environment_note}
            </p>
          )}
        </section>

        {/* Armed faults */}
        <Section title="Armed faults" testId="fl-armed">
          {armed.length === 0
            ? <p className="text-sm text-slate-500">No arms right now.</p>
            : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase text-slate-500">
                    <th className="py-1.5">Intake</th>
                    <th>Mode</th>
                    <th>Expires</th>
                    <th>By</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {armed.map(a => (
                    <tr key={a.arm_id} className="border-t border-slate-100">
                      <td className="py-1.5 font-mono">{a.intake_id}</td>
                      <td>{a.mode}</td>
                      <td className="text-[11px] text-slate-600">{a.expires_at}</td>
                      <td className="text-[11px] text-slate-600">{a.armed_by || "—"}</td>
                      <td className="text-right">
                        <Button size="sm" variant="outline"
                                onClick={() => doDisarm(a.intake_id)}
                                data-testid={`fl-disarm-${a.intake_id}`}>
                          <ZapOff className="w-3.5 h-3.5 mr-1" />Disarm
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          <div className="mt-3 flex items-center gap-2">
            <input
              placeholder='Type "CONFIRM" to disarm all'
              className="px-2 py-1 border border-slate-300 rounded text-sm w-56"
              value={disarmAllConfirm}
              onChange={e => setDisarmAllConfirm(e.target.value)}
              data-testid="fl-disarm-all-confirm"
            />
            <Button size="sm" variant="outline"
                    onClick={doDisarmAll}
                    disabled={disarmAllConfirm !== "CONFIRM"}
                    data-testid="fl-disarm-all-btn">
              Disarm all
            </Button>
          </div>
        </Section>

        {/* Arm */}
        <Section title="Arm a fault" testId="fl-arm-section" icon={Zap}>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-sm">
            <TextField label="Intake ID (synthetic)" value={armForm.intake_id}
                       onChange={v => setArmForm(f => ({ ...f, intake_id: v }))} mono
                       testId="fl-arm-intake" />
            <SelectField label="Mode" value={armForm.mode}
                         options={MODE_OPTIONS}
                         onChange={v => setArmForm(f => ({ ...f, mode: v }))}
                         testId="fl-arm-mode" />
            <TextField label="TTL (minutes, 1–240)" value={armForm.ttl_minutes}
                       type="number"
                       onChange={v => setArmForm(f => ({ ...f, ttl_minutes: Number(v) || 60 }))}
                       testId="fl-arm-ttl" />
            <TextField label="Note (optional)" value={armForm.note}
                       onChange={v => setArmForm(f => ({ ...f, note: v }))}
                       testId="fl-arm-note" />
          </div>
          <div className="mt-3">
            <Button className="bg-amber-600 hover:bg-amber-700 text-white"
                    size="sm" onClick={doArm} data-testid="fl-arm-btn">
              <Zap className="w-3.5 h-3.5 mr-1.5" />Arm fault
            </Button>
            <p className="text-[11px] text-slate-500 mt-2">
              Synthetic prefix required: <code>{SYNTH_HINT}</code>
            </p>
          </div>
        </Section>

        {/* Seed projection */}
        <Section title="Seed synthetic projection" testId="fl-seed-section" icon={Database}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
            <TextField label="Intake ID" value={seedForm.intake_id}
                       onChange={v => setSeedForm(f => ({ ...f, intake_id: v }))} mono
                       testId="fl-seed-intake" />
            <TextField label="State code" value={seedForm.state_code}
                       onChange={v => setSeedForm(f => ({ ...f, state_code: v }))}
                       testId="fl-seed-state" />
            <TextField label="Registration" value={seedForm.registration}
                       onChange={v => setSeedForm(f => ({ ...f, registration: v }))}
                       testId="fl-seed-reg" />
            <TextField label="GH jobsheet ID" value={seedForm.gh_jobsheet_id}
                       onChange={v => setSeedForm(f => ({ ...f, gh_jobsheet_id: v }))}
                       testId="fl-seed-jobsheet-id" />
            <TextField label="GH jobsheet number" value={seedForm.gh_jobsheet_number}
                       onChange={v => setSeedForm(f => ({ ...f, gh_jobsheet_number: v }))}
                       testId="fl-seed-jobsheet-no" />
          </div>
          <div className="mt-3">
            <Button size="sm" onClick={doSeed} data-testid="fl-seed-btn">
              <Plus className="w-3.5 h-3.5 mr-1.5" />Seed projection
            </Button>
          </div>
        </Section>

        {/* Emit event */}
        <Section title="Emit synthetic event" testId="fl-emit-section" icon={Rocket}>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-sm">
            <TextField label="Intake ID" value={eventForm.intake_id}
                       onChange={v => setEventForm(f => ({ ...f, intake_id: v }))} mono
                       testId="fl-emit-intake" />
            <TextField label="Event type" value={eventForm.event_type}
                       onChange={v => setEventForm(f => ({ ...f, event_type: v }))}
                       testId="fl-emit-type" />
            <TextField label="Hint state" value={eventForm.hint_state}
                       onChange={v => setEventForm(f => ({ ...f, hint_state: v }))}
                       testId="fl-emit-hint" />
            <TextField label="Reason" value={eventForm.reason}
                       onChange={v => setEventForm(f => ({ ...f, reason: v }))}
                       testId="fl-emit-reason" />
          </div>
          <div className="mt-3">
            <Button size="sm" className="bg-sky-600 hover:bg-sky-700 text-white"
                    onClick={doEmit} data-testid="fl-emit-btn">
              <Rocket className="w-3.5 h-3.5 mr-1.5" />Emit event
            </Button>
            <p className="text-[11px] text-slate-500 mt-2">
              A fresh sequence is allocated server-side. The cursor is never rewound.
            </p>
          </div>
        </Section>

        {/* Cleanup */}
        <Section title="Cleanup synthetic rows" testId="fl-cleanup-section" icon={Trash2}>
          <label className="block text-[10px] uppercase text-slate-500 font-bold mb-1">
            Intake IDs (space or comma separated)
          </label>
          <textarea
            className="w-full px-2 py-1 border border-slate-300 rounded text-sm font-mono min-h-16"
            value={cleanupIds}
            onChange={e => setCleanupIds(e.target.value)}
            placeholder="preview_synth_abc_alpha preview_synth_abc_beta ..."
            data-testid="fl-cleanup-ids"
          />
          <div className="mt-2 flex gap-2">
            <Button size="sm" variant="outline"
                    onClick={() => doCleanup({ all: false })}
                    data-testid="fl-cleanup-selected-btn">
              Clean listed
            </Button>
            <Button size="sm" variant="destructive"
                    onClick={() => doCleanup({ all: true })}
                    data-testid="fl-cleanup-all-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1.5" />Clean ALL synthetic
            </Button>
          </div>
          <p className="text-[11px] text-slate-500 mt-2">
            Cleanup filters to synthetic-prefix IDs server-side — real customer rows cannot be
            removed through this control.
          </p>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, testId, icon: Icon, children }) {
  return (
    <section className="bg-white rounded-xl border border-slate-200 p-5 mb-5 shadow-sm"
             data-testid={testId}>
      <h2 className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-3 flex items-center gap-1.5">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        {title}
      </h2>
      {children}
    </section>
  );
}

function KV({ label, value, mono, tone }) {
  const toneCls = tone === "good"
    ? "text-emerald-700"
    : tone === "bad" ? "text-red-700" : "text-slate-800";
  return (
    <div className="bg-slate-50 rounded border border-slate-200 px-2 py-1">
      <p className="text-[9px] uppercase text-slate-500">{label}</p>
      <p className={`${mono ? "font-mono" : ""} text-[12px] ${toneCls}`}>{String(value ?? "—")}</p>
    </div>
  );
}

function TextField({ label, value, onChange, mono, testId, type = "text" }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase text-slate-500 font-bold mb-1">{label}</span>
      <input
        type={type}
        value={value ?? ""}
        onChange={e => onChange(e.target.value)}
        className={`w-full px-2 py-1 border border-slate-300 rounded text-sm ${mono ? "font-mono" : ""}`}
        data-testid={testId}
      />
    </label>
  );
}

function SelectField({ label, value, options, onChange, testId }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase text-slate-500 font-bold mb-1">{label}</span>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full px-2 py-1 border border-slate-300 rounded text-sm"
        data-testid={testId}
      >
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}
